import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Feather, Recycle, Scale, Truck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { useSEO } from '../../hooks/useSEO';
// Images
const HERO_IMAGE = 'https://cdn.magicpatterns.com/uploads/iSsLTMw4wKgenTWkbWEAxu/_DSC8170.webp';
const DETAIL_IMAGE_1 = 'https://cdn.magicpatterns.com/uploads/7z3is2BCgabuUutCmURgeJ/_DSC8265.webp';
const DETAIL_IMAGE_2 = 'https://cdn.magicpatterns.com/uploads/h58wUekQZycBGPqmB5effo/682effc0ba43af376950f23b_renders_robeta.jpg';
export function AirFurniturePage() {
  useSEO({
    title: 'Air Furniture Technology™ — Strength Without the Weight',
    description: "Robeta's proprietary Air Furniture Technology uses recycled PET materials to create ultra-lightweight, durable furniture that saves 220kg vs traditional builds."
  });
  return <main className="bg-white min-h-screen" data-id="element-805">
      {/* 1. FULL-SCREEN HERO */}
      <section className="relative min-h-screen w-full flex flex-col justify-between overflow-hidden" data-id="element-806">
        <div className="absolute inset-0 z-0" data-id="element-807">
          <img src={HERO_IMAGE} alt="Air Furniture Technology Hero" className="w-full h-full object-cover" data-id="element-808" />
          <div className="absolute inset-0 bg-black/50" data-id="element-809" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-black/20" data-id="element-810" />
        </div>

        <div className="container mx-auto px-6 md:px-12 relative z-10 pt-32" data-id="element-811">
          <Breadcrumbs items={[{
          label: 'Home',
          href: '/'
        }, {
          label: 'Robeta Difference',
          href: '/robeta-difference'
        }, {
          label: 'Air Furniture'
        }]} variant="light" data-id="element-812" />
        </div>

        <div className="container mx-auto px-6 md:px-12 relative z-10 pb-24 md:pb-32" data-id="element-813">
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="max-w-4xl" data-id="element-814">
            <span className="text-robeta-copper text-sm font-bold uppercase tracking-wide-editorial block mb-6" data-id="element-815">
              Proprietary Innovation
            </span>
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white uppercase tracking-editorial leading-[0.9] mb-8" data-id="element-816">
              Air Furniture
              <br data-id="element-817" />
              Technology™
            </h1>
            <p className="text-xl md:text-2xl font-light text-white/80 max-w-2xl leading-relaxed" data-id="element-818">
              Strength without the weight. A revolution in campervan
              construction.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 2. INTRO SECTION */}
      <section className="py-24 md:py-32 bg-white" data-id="element-819">
        <div className="container mx-auto px-6 md:px-12" data-id="element-820">
          <div className="max-w-4xl mx-auto text-center" data-id="element-821">
            <h2 className="text-3xl md:text-5xl font-light text-robeta-black leading-tight mb-8" data-id="element-822">
              "We asked a simple question: Why must durability be{' '}
              <span className="italic font-serif text-robeta-copper" data-id="element-823">
                heavy
              </span>
              ?"
            </h2>
            <p className="text-lg text-robeta-black/60 font-light leading-relaxed max-w-2xl mx-auto" data-id="element-824">
              In the world of camper vans, weight is the enemy. Heavy plywood
              furniture reduces payload capacity, hurts fuel economy, and
              affects handling. Robeta's answer is Air Furniture Technology™—a
              composite construction method that is as strong as wood but
              lighter than air.
            </p>
          </div>
        </div>
      </section>

      {/* 3. DEEP DIVE 1: RECYCLED INNOVATION */}
      <section className="py-20 md:py-32 bg-robeta-mist/10" data-id="element-825">
        <div className="container mx-auto px-6 md:px-12" data-id="element-826">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-827">
            <div className="order-2 lg:order-1" data-id="element-828">
              <span className="text-robeta-copper text-xs font-bold uppercase tracking-wide-editorial block mb-4" data-id="element-829">
                Sustainable Engineering
              </span>
              <h3 className="text-3xl md:text-4xl font-bold text-robeta-black uppercase tracking-editorial mb-6" data-id="element-830">
                From Bottles to Boards
              </h3>
              <div className="space-y-6 text-robeta-black/70 font-light leading-relaxed" data-id="element-831">
                <p data-id="element-832">
                  At the core of our Air Furniture panels is a high-tech foam
                  made from <strong data-id="element-833">recycled PET plastic bottles</strong>.
                  Approximately 2,400 bottles are upcycled to create the
                  furniture for a single Robeta van.
                </p>
                <p data-id="element-834">
                  This core is sandwiched between durable, high-pressure
                  laminate (HPL) layers, creating a composite panel that is
                  incredibly rigid yet remarkably lightweight. It is impervious
                  to water, will never rot, and is resistant to the vibrations
                  and torsion of daily travel.
                </p>
                <p data-id="element-835">
                  By choosing Air Furniture, you are not just choosing a better
                  van; you are choosing a cleaner planet.
                </p>
              </div>
            </div>
            <div className="order-1 lg:order-2 relative aspect-[4/3] overflow-hidden" data-id="element-836">
              <img src={DETAIL_IMAGE_1} alt="Digital Design of Air Furniture" className="absolute inset-0 w-full h-full object-cover" data-id="element-837" />
            </div>
          </div>
        </div>
      </section>

      {/* 4. DEEP DIVE 2: PAYLOAD */}
      <section className="py-20 md:py-32 bg-white" data-id="element-838">
        <div className="container mx-auto px-6 md:px-12" data-id="element-839">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-840">
            <div className="relative aspect-[4/3] overflow-hidden" data-id="element-841">
              <img src={DETAIL_IMAGE_2} alt="Robeta Van Exterior" className="absolute inset-0 w-full h-full object-cover" data-id="element-842" />
            </div>
            <div data-id="element-843">
              <span className="text-robeta-copper text-xs font-bold uppercase tracking-wide-editorial block mb-4" data-id="element-844">
                Performance
              </span>
              <h3 className="text-3xl md:text-4xl font-bold text-robeta-black uppercase tracking-editorial mb-6" data-id="element-845">
                The Payload Advantage
              </h3>
              <div className="space-y-6 text-robeta-black/70 font-light leading-relaxed" data-id="element-846">
                <p data-id="element-847">
                  The math is simple: Air Furniture saves approximately{' '}
                  <strong data-id="element-848">200 to 220 kg (440-485 lbs)</strong> compared to a
                  standard plywood build.
                </p>
                <p data-id="element-849">
                  That is 220 kg of extra freedom. It means you can carry more
                  water, more batteries, more sports gear, and more provisions
                  without exceeding your vehicle's Gross Vehicle Weight Rating
                  (GVWR).
                </p>
                <p data-id="element-850">
                  A lighter vehicle also handles better in corners, stops
                  shorter, and consumes less fuel. It puts less strain on the
                  suspension and brakes, reducing maintenance costs over the
                  life of the vehicle.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. KEY BENEFITS GRID */}
      <section className="py-24 bg-robeta-black text-white" data-id="element-851">
        <div className="container mx-auto px-6 md:px-12" data-id="element-852">
          <div className="text-center mb-16" data-id="element-853">
            <h2 className="text-3xl md:text-4xl font-bold uppercase tracking-editorial mb-4" data-id="element-854">
              Lightness by Design
            </h2>
            <div className="w-24 h-1 bg-robeta-copper mx-auto" data-id="element-855" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-856">
            {[{
            icon: Feather,
            title: 'Ultra Lightweight',
            desc: 'Saves ~220kg vs traditional builds, maximizing your usable payload.'
          }, {
            icon: Recycle,
            title: 'Eco-Friendly',
            desc: 'Up to 2,400 recycled PET bottles are used in every vehicle.'
          }, {
            icon: Scale,
            title: 'Better Handling',
            desc: 'Lower center of gravity and less mass improves driving dynamics.'
          }, {
            icon: Truck,
            title: 'Durability',
            desc: 'Water-resistant, rot-proof, and engineered to last a lifetime.'
          }].map((item, i) => <div key={i} className="bg-white/5 p-8 border border-white/10 hover:border-robeta-copper/50 transition-colors" data-id="element-857">
                <item.icon className="w-10 h-10 text-robeta-copper mb-6" data-id="element-858" />
                <h3 className="text-xl font-bold uppercase tracking-wide mb-4" data-id="element-859">
                  {item.title}
                </h3>
                <p className="text-white/60 font-light leading-relaxed text-sm" data-id="element-860">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 6. NEXT PILLAR NAV */}
      <section className="border-t border-robeta-black/10 bg-white" data-id="element-861">
        <div className="container mx-auto px-6 md:px-12 py-16 md:py-24" data-id="element-862">
          <div className="flex flex-col items-center text-center" data-id="element-863">
            <span className="text-robeta-black/40 text-xs font-bold uppercase tracking-widest mb-4" data-id="element-864">
              Next Pillar
            </span>
            <Link to="/robeta-difference/premium-interiors" className="group flex flex-col items-center" data-id="element-865">
              <h2 className="text-4xl md:text-6xl font-bold text-robeta-black uppercase tracking-editorial mb-6 group-hover:text-robeta-copper transition-colors" data-id="element-866">
                Premium Interiors
              </h2>
              <div className="flex items-center text-sm font-bold uppercase tracking-widest text-robeta-black group-hover:text-robeta-copper transition-colors" data-id="element-867">
                Step Inside <ArrowRight className="ml-2 w-4 h-4" data-id="element-868" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* 7. CTA SECTION */}
      <section className="py-28 md:py-36 bg-robeta-mist/20 text-center" data-id="element-869">
        <div className="container mx-auto px-6 md:px-12" data-id="element-870">
          <h2 className="text-4xl md:text-5xl font-bold text-robeta-black uppercase tracking-editorial mb-8" data-id="element-871">
            Feel the Difference
          </h2>
          <p className="text-lg text-robeta-black/60 font-light max-w-2xl mx-auto mb-12" data-id="element-872">
            Test drive a Robeta and experience the agility of a lighter van.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-6" data-id="element-873">
            <Link to="/dealer-network" className="px-10 py-4 bg-robeta-black text-white font-bold uppercase tracking-widest hover:bg-robeta-copper transition-colors min-w-[200px]" data-id="element-874">
              Find a Dealer
            </Link>
            <Link to="/models" className="px-10 py-4 border border-robeta-black text-robeta-black font-bold uppercase tracking-widest hover:bg-white transition-colors min-w-[200px]" data-id="element-875">
              View Models
            </Link>
          </div>
        </div>
      </section>
    </main>;
}